import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  deleteManualModelPrice,
  getModelPriceHistory,
  listModelPrices,
  listUnresolvedModels,
  upsertManualModelPrice,
  type ModelPriceHistoryEntryResponse,
  type ModelPriceResponse,
  type ProviderType,
  type UnresolvedModelsResponse,
  type UpsertManualModelPriceRequest,
} from '../api';

/** List of every catalog row (manual + cached + seeded), sorted by provider then model. */
export function useModelPrices(): UseQueryResult<ModelPriceResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.modelPrices(),
    queryFn: ({ signal }) => listModelPrices(signal),
    staleTime: 60_000,
  });
}

/** Creates or replaces the manual override row for a (providerType, modelName) pair. */
export function useUpsertManualModelPrice(): ReturnType<
  typeof useMutation<ModelPriceResponse, Error, UpsertManualModelPriceRequest>
> {
  const queryClient = useQueryClient();
  return useMutation<ModelPriceResponse, Error, UpsertManualModelPriceRequest>({
    mutationFn: (request) => upsertManualModelPrice(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.modelPrices() });
    },
  });
}

/**
 * Reads the append-only snapshot history for a single (providerType, modelName) pair. Enabled
 * lazily so the chart query only runs when the admin opens the modal.
 */
export function useModelPriceHistory(
  providerType: ProviderType | null,
  modelName: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<ModelPriceHistoryEntryResponse[]> {
  const enabled = Boolean(providerType) && Boolean(modelName) && (options?.enabled ?? true);
  return useQuery({
    queryKey:
      providerType && modelName
        ? AdminQueryKeys.modelPriceHistory(providerType, modelName)
        : ['admin', 'model-prices', '(none)', 'history'],
    queryFn: ({ signal }) => getModelPriceHistory(providerType as ProviderType, modelName as string, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Removes the manual override row so the cascade falls back to OpenRouter/Seed. */
export function useDeleteManualModelPrice(): ReturnType<
  typeof useMutation<null, Error, { providerType: ProviderType; modelName: string }>
> {
  const queryClient = useQueryClient();
  return useMutation<null, Error, { providerType: ProviderType; modelName: string }>({
    mutationFn: ({ providerType, modelName }) => deleteManualModelPrice(providerType, modelName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.modelPrices() });
    },
  });
}

/**
 * Lists the (providerType, modelName) pairs the resolver still can't price in the last
 * <code>windowDays</code> days (default 30). Used by the banner on the pricing page to surface
 * models that need a manual override.
 */
export function useUnresolvedModels(
  windowDays?: number,
): UseQueryResult<UnresolvedModelsResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.unresolvedModels(windowDays ?? null),
    queryFn: ({ signal }) => listUnresolvedModels(windowDays, signal),
    staleTime: 60_000,
  });
}
